import * as Sentry from '@sentry/react';
import type { Action, Middleware, Reducer, ReducersMapObject } from 'redux';

/**
 * Redux error #3 ("getState while the reducer is executing") reaches us with no reducer on the
 * stack: it is thrown from an event handler or a React render (SHOP-WEB-E / -W / -2N / -36). Redux
 * clears its `isDispatching` flag in a `finally`, so the dispatch that set it never finished.
 * Either the browser ran other tasks in the middle of a reducer, or the script was terminated
 * mid-reducer (a crawler's time budget, Firefox's "stop script"). A terminated script skips
 * every `finally`, and the store then refuses getState() for the rest of the page's life.
 *
 * `trackReducer` records which action was being reduced, the same way: set before, cleared in a
 * `finally`. Whatever leaves Redux's flag set leaves this one set too, so the #3 event can name
 * the action (see sentryStateEventProcessor).
 *
 * The rest tells the two causes apart:
 * - `trackSlices` names the slice reducer that was running (`phase`);
 * - `trackDispatch` keeps the chain of dispatches in progress and where the outermost one came from;
 * - the re-entry probe records any DOM event delivered while a reducer is running, which is only
 *   possible if the reducer yielded (a nested event loop) or triggered it synchronously;
 * - a timer heartbeat tells a suspended reducer from a dead one. A reducer that returns normally
 *   never spans a timer task, and Firefox suspends a window's timers while it spins a nested event
 *   loop (sync XHR, alert). So once a heartbeat has fired since the reducer started, the reducer
 *   is no longer on the stack and the in-flight marker is stale: the script was killed. Events after
 *   that are counted (`stale_events`), not recorded as re-entries;
 * - a reducer that returns after its getState() was refused proves the reducer was suspended, not
 *   killed: it is reported once as `[redux] stuck dispatch resumed`.
 *
 * What kills it on real visitors is Firefox leaving the page. A load from the address bar (or Back /
 * Forward to another site) first asks the tab's content process to cancel the page's running JS,
 * so a busy page cannot hold up the navigation (`HangMonitorChild::InterruptCallback` in
 * dom/ipc/ProcessHangMonitor.cpp returns false: uncatchable, no `finally`). The old page keeps
 * running until the next one commits, and its first getState() throws #3. Every #3 since this probe
 * shipped was Firefox and `stale_inflight`, the visitor's focus was outside the page (the address
 * bar) when the reducer died, and 9 of 11 stuck-store reloads never came back: those visitors had
 * gone where they were navigating to. Those errors are dropped; see isKilledStoreError.
 */

type Reentry = { type: string; after_ms: number };

/**
 * What the re-entry probe concluded, as the `redux_reentry` tag:
 * - event types (`visibilitychange,blur`): DOM events reached us while the reducer was suspended;
 * - `stale_inflight`: a heartbeat fired after the reducer started, so it was killed, not suspended;
 * - `none`: still the reducer's own task, with no event in between: our code re-entered the store
 *   synchronously.
 */
export type ReentryVerdict = string;

type InFlight = {
  type: string;
  startedAt: number;
  phase: string;
  visibility: string | undefined;
  focused: boolean | undefined;
  reentries: Reentry[];
  refusedAt: number | null;
  refusedPhase: string | null;
  startTick: number;
  staleAt: number | null;
  staleEvents: number;
};

export type InFlightSnapshot = {
  action: string;
  elapsed_ms: number;
  phase: string;
  dispatch_chain: string[];
  visibility_at_start: string | undefined;
  focused_at_start: boolean | undefined;
  visibility_now: string | undefined;
  focused_now: boolean | undefined;
  reentries: Reentry[];
  stale: boolean;
  stale_since_ms: number | null;
  stale_events: number;
  origin_stack: string | undefined;
};

const MAX_REENTRIES = 8;
const MAX_STACK_FRAMES = 15;

let inFlight: InFlight | null = null;
const dispatchChain: string[] = [];
let dispatchOrigin: Error | null = null;

let tick = 0;
let heartbeatPending = false;

/** At most one timer outstanding, however many dispatches run in a task. */
function armHeartbeat(): void {
  if (heartbeatPending || typeof setTimeout === 'undefined') {
    return;
  }

  heartbeatPending = true;
  setTimeout(() => {
    heartbeatPending = false;
    tick += 1;

    // Any reducer still marked in flight when a timer runs has left the stack.
    if (inFlight && inFlight.staleAt === null) {
      inFlight.staleAt = now();
    }
  }, 0);
}

function isStale(current: InFlight): boolean {
  return tick !== current.startTick;
}

const now = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

function visibilityState(): string | undefined {
  return typeof document !== 'undefined' ? document.visibilityState : undefined;
}

function hasFocus(): boolean | undefined {
  try {
    return typeof document !== 'undefined' ? document.hasFocus() : undefined;
  } catch {
    return undefined;
  }
}

function actionType(action: unknown): string {
  if (typeof action === 'function') {
    return 'thunk';
  }

  const type = (action as Action | undefined)?.type;

  return typeof type === 'string' ? type : 'unknown';
}

export function trackReducer<S, A extends Action>(reducer: Reducer<S, A>): Reducer<S, A> {
  return (state, action) => {
    const current: InFlight = {
      type: actionType(action),
      startedAt: now(),
      phase: 'root',
      visibility: visibilityState(),
      focused: hasFocus(),
      reentries: [],
      refusedAt: null,
      refusedPhase: null,
      startTick: tick,
      staleAt: null,
      staleEvents: 0,
    };
    inFlight = current;
    armHeartbeat();

    try {
      return reducer(state, action);
    } finally {
      inFlight = null;

      if (current.refusedAt !== null) {
        const resumedAt = now();
        // Outside the reducer: Sentry reads the store while it builds the event.
        setTimeout(() => reportResumed(current, resumedAt), 0);
      }
    }
  };
}

/** Wraps each slice reducer handed to `combineReducers`, so a stuck dispatch names its slice. */
export function trackSlices<M extends ReducersMapObject>(reducers: M): M {
  return Object.fromEntries(
    Object.entries(reducers).map(([key, reducer]) => [
      key,
      (state: unknown, action: Action) => {
        const current = inFlight;

        if (!current) {
          return reducer(state, action);
        }

        const previous = current.phase;
        current.phase = key;

        try {
          return reducer(state, action);
        } finally {
          current.phase = previous;
        }
      },
    ]),
  ) as M;
}

/**
 * Outermost middleware. Keeps the dispatches in progress (a middleware may dispatch while handling
 * another action) and an unformatted Error for the outermost one: its stack says who dispatched.
 */
export const trackDispatch: Middleware = () => (next) => (action) => {
  if (dispatchChain.length === 0) {
    dispatchOrigin = new Error('Redux dispatch origin');
  }

  dispatchChain.push(actionType(action));

  try {
    return next(action);
  } finally {
    dispatchChain.pop();

    if (dispatchChain.length === 0) {
      dispatchOrigin = null;
    }
  }
};

function recordReentry(event: Event): void {
  const current = inFlight;

  if (!current) {
    return;
  }

  if (isStale(current)) {
    current.staleEvents += 1;
  } else if (current.reentries.length < MAX_REENTRIES) {
    current.reentries.push({ type: event.type, after_ms: Math.round(now() - current.startedAt) });
  }
}

const REENTRY_EVENTS = [
  'visibilitychange',
  'focus',
  'blur',
  'pageshow',
  'pagehide',
  'message',
  'storage',
  'popstate',
  'pointerdown',
  'keydown',
  'click',
  'online',
  'offline',
];

let reentryProbeInstalled = false;

/** One capture listener per event type; it does nothing unless a reducer is running. */
export function installReentryProbe(): void {
  if (reentryProbeInstalled || typeof window === 'undefined') {
    return;
  }

  reentryProbeInstalled = true;
  REENTRY_EVENTS.forEach((type) => window.addEventListener(type, recordReentry, { capture: true, passive: true }));
}

/** Query strings and fragments can carry tokens; a stack only needs the script path. */
function stripUrlParams(value: string): string {
  return value.replace(/[?#][^\s:)]*/g, '');
}

export function trimStack(stack: string | undefined): string | undefined {
  if (!stack) {
    return undefined;
  }

  const frames = stack
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !/^Error\b/.test(line))
    .slice(0, MAX_STACK_FRAMES)
    .map((line) => stripUrlParams(line).slice(0, 200));

  return frames.length ? frames.join('\n') : undefined;
}

function snapshotOf(current: InFlight): InFlightSnapshot {
  return {
    action: current.type,
    elapsed_ms: Math.round(now() - current.startedAt),
    phase: current.phase,
    dispatch_chain: [...dispatchChain],
    visibility_at_start: current.visibility,
    focused_at_start: current.focused,
    visibility_now: visibilityState(),
    focused_now: hasFocus(),
    reentries: [...current.reentries],
    stale: isStale(current),
    stale_since_ms: current.staleAt === null ? null : Math.round(current.staleAt - current.startedAt),
    stale_events: current.staleEvents,
    origin_stack: trimStack(dispatchOrigin?.stack),
  };
}

export function reentryVerdict(snapshot: InFlightSnapshot): ReentryVerdict {
  if (snapshot.reentries.length) {
    return [...new Set(snapshot.reentries.map((reentry) => reentry.type))].join(',');
  }

  return snapshot.stale ? 'stale_inflight' : 'none';
}

/** Test seam: leaves a dispatch in flight the way a script killed mid-reducer does. */
export function abandonReducerForTests(type: string): void {
  inFlight = {
    type,
    startedAt: now(),
    phase: 'root',
    visibility: visibilityState(),
    focused: hasFocus(),
    reentries: [],
    refusedAt: null,
    refusedPhase: null,
    startTick: tick,
    staleAt: null,
    staleEvents: 0,
  };
  armHeartbeat();
}

export function inFlightReducerAction(): InFlightSnapshot | null {
  return inFlight ? snapshotOf(inFlight) : null;
}

/** Raw stack of the outermost dispatch in progress, for Sentry to parse and symbolicate. */
export function dispatchOriginStack(): string | undefined {
  return dispatchOrigin?.stack;
}

/**
 * Called when the store refused a read. Marks the dispatch, so that if its reducer ever returns we
 * know it was suspended rather than killed.
 */
export function noteGetStateRefused(): InFlightSnapshot | null {
  if (!inFlight) {
    return null;
  }

  if (inFlight.refusedAt === null) {
    inFlight.refusedAt = now();
    inFlight.refusedPhase = inFlight.phase;
  }

  return snapshotOf(inFlight);
}

function reportResumed(dispatch: InFlight, resumedAt: number): void {
  Sentry.captureMessage('[redux] stuck dispatch resumed', {
    level: 'info',
    fingerprint: ['redux', 'stuck_dispatch_resumed'],
    tags: { redux_in_flight_action: dispatch.type, redux_reducer_phase: dispatch.refusedPhase ?? dispatch.phase },
    contexts: {
      redux_dispatch: {
        action: dispatch.type,
        refused_after_ms: Math.round((dispatch.refusedAt ?? resumedAt) - dispatch.startedAt),
        resumed_after_ms: Math.round(resumedAt - dispatch.startedAt),
        visibility_at_start: dispatch.visibility,
        focused_at_start: dispatch.focused,
        reentries: dispatch.reentries,
        stale_events: dispatch.staleEvents,
      },
    },
  });
}

const GET_STATE_WHILE_REDUCING =
  /Minified Redux error #3;|You may not call store\.getState\(\) while the reducer is executing/;

export function isGetStateWhileReducingError(message: unknown): boolean {
  return typeof message === 'string' && GET_STATE_WHILE_REDUCING.test(message);
}

/** Everything Redux refuses while `isDispatching` is set: getState (#3), subscribe (#5), unsubscribe (#6), dispatch (#9). */
const REFUSED_WHILE_REDUCING =
  /Minified Redux error #[3569];|while the reducer is executing|^Reducers may not dispatch actions\./;

let killedStoreReported = false;

/**
 * True for an error the store throws only because Firefox killed a reducer while the visitor was
 * navigating away (see the header). The verdict needs the heartbeat: a dispatch still marked in
 * flight after a timer ran is one whose script was terminated. Any other #3 still reports. The first
 * one per page is kept as a log line, so the rate stays visible without opening an issue.
 */
export function isKilledStoreError(message: unknown): boolean {
  if (
    typeof message !== 'string' ||
    !REFUSED_WHILE_REDUCING.test(message) ||
    !inFlight ||
    !isStale(inFlight) ||
    typeof navigator === 'undefined' ||
    !/Firefox\//.test(navigator.userAgent)
  ) {
    return false;
  }

  if (!killedStoreReported) {
    killedStoreReported = true;
    const snapshot = snapshotOf(inFlight);

    Sentry.logger.info('[redux] reducer killed by a Firefox navigation', {
      action: snapshot.action,
      phase: snapshot.phase,
      elapsed_ms: snapshot.elapsed_ms,
      stale_since_ms: snapshot.stale_since_ms ?? -1,
      stale_events: snapshot.stale_events,
      focused_at_start: String(snapshot.focused_at_start),
      focused_now: String(snapshot.focused_now),
      visibility_now: snapshot.visibility_now ?? '',
    });
  }

  return true;
}

/** Test seam: lets each test see the first killed-store error of a page again. */
export function resetKilledStoreReportForTests(): void {
  killedStoreReported = false;
}

const RELOAD_GUARD_KEY = 'foodamigos:redux-stuck-reload-at';
const RELOAD_GUARD_MS = 60_000;
const RELOADED_MARKER_KEY = 'foodamigos:redux-stuck-reloaded';
const RELOADED_MARKER_MAX_AGE_MS = 5 * 60_000;

/**
 * Error boundary hook: a store stuck in "dispatching" can never render again, so reload the page
 * instead of leaving the customer on the error screen. At most once a minute per tab, so a #3 our
 * own code causes on every load shows the error screen rather than a reload loop. The Sentry event
 * is flushed first — it carries the in-flight action this instrumentation exists for.
 */
export function reloadIfStoreStuck(error: unknown, _componentStack?: unknown, eventId?: string): boolean {
  if (!isGetStateWhileReducingError((error as Error)?.message)) {
    return false;
  }

  const timestamp = Date.now();
  const action = inFlight?.type ?? 'none';

  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY));

    if (lastReloadAt && timestamp - lastReloadAt < RELOAD_GUARD_MS) {
      Sentry.setTag('redux_stuck_reload', 'skipped_recent');

      return false;
    }

    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(timestamp));
    window.sessionStorage.setItem(
      RELOADED_MARKER_KEY,
      JSON.stringify({ at: timestamp, action, event_id: typeof eventId === 'string' ? eventId : undefined }),
    );
  } catch {
    // No sessionStorage, no loop guard: keep the error screen.
    Sentry.setTag('redux_stuck_reload', 'no_storage');

    return false;
  }

  Sentry.setTag('redux_stuck_reload', 'attempted');
  Sentry.addBreadcrumb({ category: 'redux', level: 'warning', message: 'store stuck, reloading', data: { action } });
  Sentry.flush(2000).finally(() => window.location.reload());

  return true;
}

/**
 * On boot: if this page load is the reload `reloadIfStoreStuck` asked for, say so in a log line and
 * tag every later event of this page, so a #3 that comes back after the reload is visible as such.
 */
export function reportStuckStoreReload(): void {
  let marker: { at?: unknown; action?: unknown; event_id?: unknown } | null = null;

  try {
    const raw = window.sessionStorage.getItem(RELOADED_MARKER_KEY);

    if (!raw) {
      return;
    }

    window.sessionStorage.removeItem(RELOADED_MARKER_KEY);
    marker = JSON.parse(raw);
  } catch {
    return;
  }

  const at = Number(marker?.at);

  if (!at || Date.now() - at > RELOADED_MARKER_MAX_AGE_MS) {
    return;
  }

  const attrs = {
    action: typeof marker?.action === 'string' ? marker.action : 'none',
    previous_event_id: typeof marker?.event_id === 'string' ? marker.event_id : '',
    reload_ms: Date.now() - at,
  };

  Sentry.setTag('redux_stuck_reload', 'recovered');
  Sentry.addBreadcrumb({ category: 'redux', message: 'page reloaded after stuck store', data: attrs });
  Sentry.logger.info('[redux] reloaded after stuck store', attrs);
}
